import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import moment from 'moment';

import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import IconBo from 'assets/img/icons/acms-bo.svg';
import IconMe from 'assets/img/icons/acms-me.svg';
import IconRecruit from 'assets/img/icons/acms-recruit.svg';
import IconWorkFlow from 'assets/img/icons/acms-work-flow.svg';
import IconWiki from 'assets/img/icons/acms-wiki.svg';
import IconProject from 'assets/img/icons/acms-project.svg';
import IconBom from 'assets/img/icons/acms-bom.svg';
import IconOkr from 'assets/img/icons/acms-okr.svg';
import imgAvatar from 'assets/img/theme/avatar_default.png';
import NotificationIcon from 'assets/img/icons/notitification.svg';

import configs from 'config';
import styles from './style.module.scss';
import { ThemContext } from 'layouts/Admin';
import { customDate } from 'helpers/formatDate';
import {
  getUrlNotificationToWiki,
  getUrlNotificationToRecruit,
  getUrlNotificationToProject,
} from 'helpers/notification';
import { useGetNotifications, useGetTotalNotifications } from 'hook/useNotifications';
import { updateNotificationView, updateAllNotificationReadAll, updateNotificationRead } from 'api/notification';
import {
  DEFAULT_PAGE,
  SYSTEM_ME,
  SYSTEM_BO,
  SYSTEM_RECRUIT,
  SYSTEM_WORKFLOW,
  USE_GET_NOTIFICATIONS,
  USE_TOTAL_GET_NOTIFICATIONS,
  DEFAULT_LIMIT_LARGE,
  SYSTEM_WIKI,
  SYSTEM_PROJECT,
  SYSTEM_OKR,
  SYSTEM_BOM,
} from 'constants/index.js';
import Loading from 'components/Loading';

const NotificationAgain = (prop) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dataInfoUser } = useContext(ThemContext);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [systemId, setSystemId] = useState(SYSTEM_ME);
  const [total, setTotal] = useState(0);
  const [listNotification, setListNotification] = useState({ today: [], orther: [] });

  const { data: dataNotifications, isLoading } = useGetNotifications({
    user_id: dataInfoUser?.user_id,
    system_id: systemId,
    page: page,
    size: DEFAULT_LIMIT_LARGE,
  });

  const { data: dataTotalNotifications } = useGetTotalNotifications({
    user_id: dataInfoUser?.user_id,
  });

  useEffect(() => {
    if (dataTotalNotifications) {
      let total = 0;
      dataTotalNotifications.forEach((item) => {
        if (item?.system_id > 0) {
          total += item?.total;
        }
      });
      setTotal(total);
    }
  }, [dataTotalNotifications]);

  window.OneSignal = window.OneSignal || [];
  const OneSignal = window.OneSignal;

  React.useEffect(() => {
    if (dataInfoUser?.user_id) {
      OneSignal.push(function () {
        OneSignal.init({
          // allowLocalhostAsSecureOrigin: true,
          appId: configs.ID_ONESIGNAL,
          safari_web_id: configs.ID_SAFARI_ID,
          notifyButton: {
            enable: true,
          },
        });
        OneSignal.push(function () {
          OneSignal.isPushNotificationsEnabled(function (isEnabled) {
            if (isEnabled) console.log('Push notifications are already enabled!');
            else console.log('Push notifications are not enabled yet.');
            OneSignal.showHttpPrompt();
          });
        });
        OneSignal.setExternalUserId(String(dataInfoUser?.user_id));
        OneSignal.getExternalUserId().then();
        OneSignal.on('notificationDisplay', function (event) {
          queryClient.invalidateQueries(USE_GET_NOTIFICATIONS);
          queryClient.invalidateQueries(USE_TOTAL_GET_NOTIFICATIONS);
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInfoUser]);

  const updateAllNotification = async (systemIdChange) => {
    if (total > 0) {
      // eslint-disable-next-line
      await updateNotificationView({ user_id: dataInfoUser?.user_id, system_id: systemIdChange ?? systemId });
      queryClient.invalidateQueries(USE_TOTAL_GET_NOTIFICATIONS);
    }
  };

  const [open, setOpen] = React.useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    if (total > 0) {
      updateAllNotification();
    }
    setOpen(!open);
  };

  const handleOnError = (e) => {
    e.target.src = imgAvatar;
  };

  useEffect(() => {
    if (dataNotifications?.list_noti?.length > 0) {
      let today = [];
      let orther = [];
      setListNotification((prev) => {
        dataNotifications?.list_noti.forEach((element) => {
          if (customDate(element?.created_at, 'DD/MM/YYYY') === moment().format('DD/MM/YYYY')) {
            today.push(element);
          } else {
            orther.push(element);
          }
        });

        return {
          today: [...prev?.today, ...today],
          orther: [...prev?.orther, ...orther],
        };
      });
    }
  }, [dataNotifications]);

  const fetchMore = () => {
    if (!isLoading) setPage((value) => value + 1);
  };

  const handleUpdateNotificationRead = async (idNotification) => {
    await updateNotificationRead({ id: idNotification, user_id: dataInfoUser?.user_id });
    setListNotification((prev) => {
      prev?.today?.forEach((element) => {
        if (element?.id === idNotification) {
          element.is_read = 1;
        }
      });
      prev?.orther?.forEach((element) => {
        if (element?.id === idNotification) {
          element.is_read = 1;
        }
      });
      return {
        today: [...prev?.today],
        orther: [...prev?.orther],
      };
    });
    queryClient.removeQueries(USE_TOTAL_GET_NOTIFICATIONS);
  };

  const changeTab = async (systemIdChange) => {
    if (Number(systemIdChange) !== Number(systemId)) {
      setSystemId(systemIdChange);
      setPage(DEFAULT_PAGE);
      setListNotification({ today: [], orther: [] });
      await updateAllNotification(systemIdChange);
    }
  };

  const seenAll = async () => {
    await updateAllNotificationReadAll({ user_id: dataInfoUser?.user_id, system_id: systemId });
    queryClient.removeQueries(USE_GET_NOTIFICATIONS);
    setPage(DEFAULT_PAGE);
    setListNotification({ today: [], orther: [] });
  };

  const getUrl = (type, requestID, item) => {
    switch (systemId) {
      case SYSTEM_ME:
        return configs.DOMAIN_AMS_ME + '/timekeeping/time-sheets';
      case SYSTEM_WIKI:
        return getUrlNotificationToWiki(type, item);
      case SYSTEM_WORKFLOW:
        return configs.DOMAIN_AMS_WORKFLOW + '/request?request_id=' + requestID;
      case SYSTEM_RECRUIT:
        return getUrlNotificationToRecruit(type, item);
      case SYSTEM_PROJECT:
        const linkToProject = getUrlNotificationToProject(type, item);
        return configs.DOMAIN_AMS_PROJECT + linkToProject;
      default:
        return null;
    }
  };

  const TotalNotificationsPerSystem = ({ systemId }) => {
    let total = dataTotalNotifications?.find((it) => it.system_id === systemId)?.total;
    if (!total) return <></>;
    return <p className={styles.total}>{total > 99 ? '99+' : total}</p>;
  };

  return (
    <div className={styles.menuNotification}>
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <div>
          <Tooltip
            PopperProps={{
              disablePortal: true,
            }}
            onClose={handleTooltipClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <div className={styles.layoutTooltip}>
                <div className={styles.head}>
                  <p className={styles.title}>{t('common.notification')}</p>
                  <p onClick={() => seenAll()} className={styles.seen}>
                    {t('notifications.seeAll')}
                  </p>
                </div>
                <div className={styles.notificationLayout}>
                  <div className={styles.tab}>
                    <p className={styles.titleTab}>{t('home.system')}</p>
                    <div className={styles.items}>
                      <div
                        className={`${styles.item} ${systemId === SYSTEM_BOM && styles.active}`}
                        onClick={() => changeTab(SYSTEM_BOM)}
                      >
                        <img src={IconBom} alt={IconBom} />
                        <div className={styles.itemContent}>
                          <p>{t('home.acmsBom')}</p>
                          <p>{t('home.bom')}</p>
                        </div>
                        <TotalNotificationsPerSystem systemId={SYSTEM_BOM} />
                      </div>
                      <div
                        className={`${styles.item} ${systemId === SYSTEM_OKR && styles.active}`}
                        onClick={() => changeTab(SYSTEM_OKR)}
                      >
                        <img src={IconOkr} alt={IconOkr} />
                        <div className={styles.itemContent}>
                          <p>{t('home.acmsOkr')}</p>
                          <p>{t('home.okr')}</p>
                        </div>
                        <TotalNotificationsPerSystem systemId={SYSTEM_OKR} />
                      </div>
                      <div
                        className={`${styles.item} ${systemId === SYSTEM_ME && styles.active}`}
                        onClick={() => changeTab(SYSTEM_ME)}
                      >
                        <img src={IconMe} alt={IconMe} />
                        <div className={styles.itemContent}>
                          <p>{t('home.acmsMe')}</p>
                          <p>{t('home.employeeGate')}</p>
                        </div>
                        <TotalNotificationsPerSystem systemId={SYSTEM_ME} />
                      </div>
                      <div
                        className={`${styles.item} ${systemId === SYSTEM_BO && styles.active}`}
                        onClick={() => changeTab(SYSTEM_BO)}
                      >
                        <img src={IconBo} alt={IconBo} />
                        <div className={styles.itemContent}>
                          <p>{t('home.acmsBo')}</p>
                          <p>{t('home.employee')}</p>
                        </div>
                        <TotalNotificationsPerSystem systemId={SYSTEM_BO} />
                      </div>
                      <div
                        className={`${styles.item} ${systemId === SYSTEM_WORKFLOW && styles.active}`}
                        onClick={() => changeTab(SYSTEM_WORKFLOW)}
                      >
                        <img src={IconWorkFlow} alt={IconWorkFlow} />
                        <div className={styles.itemContent}>
                          <p>{t('home.acmsWorkFlow')}</p>
                          <p>{t('home.createRequest')}</p>
                        </div>
                        <TotalNotificationsPerSystem systemId={SYSTEM_WORKFLOW} />
                      </div>
                      <div
                        className={`${styles.item} ${systemId === SYSTEM_WIKI && styles.active}`}
                        onClick={() => changeTab(5)}
                      >
                        <img src={IconWiki} alt={IconWiki} />
                        <div className={styles.itemContent}>
                          <p>{t('home.acmsWiki')}</p>
                          <p>{t('home.portal')}</p>
                        </div>
                        <TotalNotificationsPerSystem systemId={SYSTEM_WIKI} />
                      </div>
                      <div
                        className={`${styles.item} ${systemId === SYSTEM_RECRUIT && styles.active}`}
                        onClick={() => changeTab(SYSTEM_RECRUIT)}
                      >
                        <img src={IconRecruit} alt={IconRecruit} />
                        <div className={styles.itemContent}>
                          <p>{t('home.acmsRecruit')}</p>
                          <p>{t('home.recruitment')}</p>
                        </div>
                        <TotalNotificationsPerSystem systemId={SYSTEM_RECRUIT} />
                      </div>
                      <div
                        className={`${styles.item} ${systemId === SYSTEM_PROJECT && styles.active}`}
                        onClick={() => changeTab(6)}
                      >
                        <img src={IconProject} alt={IconProject} />
                        <div className={styles.itemContent}>
                          <p>{t('home.acmsProject')}</p>
                          <p>{t('home.project')}</p>
                        </div>
                        <TotalNotificationsPerSystem systemId={SYSTEM_PROJECT} />
                      </div>
                    </div>
                  </div>
                  <div className={`${styles.contentNotication}`} id={'scroll-notifications'}>
                    <InfiniteScroll
                      scrollableTarget="scroll-notifications"
                      dataLength={dataNotifications?.total_row || 0}
                      next={fetchMore}
                      hasMore={isLoading || page * DEFAULT_LIMIT_LARGE < dataNotifications?.total_row}
                      className={styles.infiniteScroll}
                    >
                      {listNotification?.today?.length === 0 &&
                        listNotification?.orther?.length === 0 &&
                        !isLoading && <p className={`${styles.error} error`}>{t('common.notData')}</p>}
                      {listNotification?.today?.length > 0 && (
                        <p className={styles.titleContent}>{t('notifications.today')}</p>
                      )}
                      {listNotification?.today?.map((item, index) => {
                        return (
                          <Link
                            to={{
                              pathname: getUrl(item?.type, item?.request_id, item),
                            }}
                            className={styles.redirect}
                            target={!getUrl(item?.type, item?.request_id, item) ? '_self' : '_blank'}
                            key={index}
                          >
                            <div
                              className={styles.itemContent}
                              onClick={() => item.is_read === 0 && handleUpdateNotificationRead(item?.id)}
                            >
                              <div className={item.is_read === 0 ? styles.unRead : styles.read}></div>
                              <div className={styles.infor}>
                                <img
                                  onError={handleOnError}
                                  alt={item?.sender_avatar ? item.sender_avatar : imgAvatar}
                                  src={item?.sender_avatar ? item.sender_avatar : imgAvatar}
                                  className={`${styles.image}`}
                                />
                                <div className={styles.inforDetail}>
                                  {item?.name && <p>{item?.name}</p>}
                                  {item?.message && (
                                    <Tooltip
                                      title={
                                        <p
                                          className={styles.tooltipMessage}
                                          dangerouslySetInnerHTML={{ __html: item.message }}
                                        ></p>
                                      }
                                      followCursor
                                    >
                                      <p dangerouslySetInnerHTML={{ __html: item.message }}></p>
                                    </Tooltip>
                                  )}
                                  <p>{customDate(new Date(item?.created_at), 'HH:mm DD/MM/YYYY')}</p>
                                </div>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                      {listNotification?.orther?.length > 0 && (
                        <p className={`${styles.titleContent} ${styles.old}`}>{t('notifications.older')}</p>
                      )}
                      {listNotification?.orther?.map((item, index) => {
                        return (
                          <Link
                            to={{
                              pathname: getUrl(item?.type, item?.request_id, item),
                            }}
                            className={styles.redirect}
                            target={!getUrl(item?.type, item?.request_id, item) ? '_self' : '_blank'}
                            key={index}
                          >
                            <div
                              className={styles.itemContent}
                              onClick={() => item.is_read === 0 && handleUpdateNotificationRead(item?.id)}
                            >
                              <div className={item.is_read === 0 ? styles.unRead : styles.read}></div>
                              <div className={styles.infor}>
                                <img
                                  onError={handleOnError}
                                  alt={item?.sender_avatar ? item.sender_avatar : imgAvatar}
                                  src={item?.sender_avatar ? item.sender_avatar : imgAvatar}
                                  className={`${styles.image}`}
                                />
                                <div className={styles.inforDetail}>
                                  {item?.name && <p>{item?.name}</p>}
                                  {item?.message && (
                                    <Tooltip
                                      title={
                                        <p
                                          className={styles.tooltipMessage}
                                          dangerouslySetInnerHTML={{ __html: item.message }}
                                        ></p>
                                      }
                                      followCursor
                                    >
                                      <p dangerouslySetInnerHTML={{ __html: item.message }}></p>
                                    </Tooltip>
                                  )}
                                  <p>{customDate(new Date(item?.created_at), 'HH:mm DD/MM/YYYY')}</p>
                                </div>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </InfiniteScroll>
                    {isLoading && <Loading addClass={styles.loading} />}
                  </div>
                </div>
              </div>
            }
            className={styles.toolTipNotification}
            arrow
          >
            <Button onClick={handleTooltipOpen} className={styles.buttonNoti}>
              {total > 0 && (
                <span className={styles.numberInfo}>
                  {total < 100 && total}
                  {total > 99 && '99+'}
                </span>
              )}
              <img alt={NotificationIcon} src={NotificationIcon} className={styles.iconNoti} />
            </Button>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default NotificationAgain;
